'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Suiting', href: '/products?category=suiting' },
  { label: 'Shirting', href: '/products?category=shirting' },
  { label: 'Blog', href: '/blog' },
  { label: 'About', href: '/#about' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-border shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-32 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" aria-label="Sopher home" className="flex items-center gap-3 group">
            <AppLogo
              size={128}
              onDarkBackground={!scrolled}
              className={`logo-breathe transition-all duration-300 group-hover:scale-105 ${
                scrolled ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'
              }`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navLinks?.map((link) => (
              <Link
                key={link?.href}
                href={link?.href}
                className={`nav-link text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  scrolled
                    ? 'text-muted-foreground hover:text-foreground'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* CTA */}
          <Link
            href="/contact"
            className={`hidden md:inline-flex items-center gap-2 px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
              scrolled
                ? 'bg-primary text-white hover:bg-secondary'
                : 'bg-white/10 text-white border border-white/30 hover:bg-white/20'
            }`}
          >
            Enquire Now
          </Link>

          {/* Hamburger */}
          <button
            className="md:hidden flex flex-col gap-[5px] p-2 z-50"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <span
              className={`block w-6 h-[1.5px] transition-all duration-300 ${
                menuOpen
                  ? 'rotate-45 translate-y-[6.5px] bg-foreground'
                  : scrolled
                    ? 'bg-foreground'
                    : 'bg-white'
              }`}
            />
            <span
              className={`block w-6 h-[1.5px] transition-all duration-300 ${
                menuOpen ? 'opacity-0 bg-foreground' : scrolled ? 'bg-foreground' : 'bg-white'
              }`}
            />
            <span
              className={`block w-6 h-[1.5px] transition-all duration-300 ${
                menuOpen
                  ? '-rotate-45 -translate-y-[6.5px] bg-foreground'
                  : scrolled
                    ? 'bg-foreground'
                    : 'bg-white'
              }`}
            />
          </button>
        </div>
      </header>
      {/* Mobile Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-white/95 backdrop-blur-lg flex flex-col justify-center items-center gap-8"
          onClick={() => setMenuOpen(false)}
        >
          {navLinks?.map((link, i) => (
            <Link
              key={link?.href}
              href={link?.href}
              onClick={() => setMenuOpen(false)}
              className="font-display text-3xl font-semibold text-foreground hover:text-primary transition-colors"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              {link?.label}
            </Link>
          ))}
          <Link href="/contact" onClick={() => setMenuOpen(false)} className="mt-4 btn-primary">
            Enquire Now
          </Link>
        </div>
      )}
    </>
  );
}
